import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  type DepositAccount,
  OrgDepositAccountsService,
} from "@/shared/services/payments/depositAccounts.service";

/**
 * The organization's own standing receiving accounts (México CLABE, Argentina CVU).
 *
 * Registered by staff and previously readable only from the staff-only config surface, which is why the
 * push-detected panels used to say "ask your account executive" instead of showing the number.
 *
 * Fails quiet: a 401/403/500 leaves the list empty and the panel falls back to that same hint, rather than
 * showing an error for something the user cannot act on.
 */
export function useOrgDepositAccounts(organizationId?: string): {
  accounts: DepositAccount[];
  isLoading: boolean;
} {
  const { user, isInternalUser } = useAuth();
  // Same rule as `useAvailableCorridors`: staff read the org they are looking at, defaulting to their own,
  // because the panels this feeds have no org selector. Without an organization the endpoint answers `[]`.
  const scopedOrgId = isInternalUser
    ? (organizationId ?? user?.organization_id ?? user?.organization?.id ?? undefined)
    : undefined;
  const canQuery = isInternalUser ? !!scopedOrgId : !!user;

  const [accounts, setAccounts] = useState<DepositAccount[]>([]);
  const [isLoading, setIsLoading] = useState(canQuery);

  useEffect(() => {
    if (!canQuery) {
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setAccounts([]);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    OrgDepositAccountsService.list(scopedOrgId)
      .then((response) => {
        if (!cancelled) setAccounts(response.accounts ?? []);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canQuery, scopedOrgId]);

  return { accounts, isLoading };
}
